import { User as FirebaseUser } from 'firebase/auth'
import { createClient } from 'urql'
import { Dispatch, SetStateAction } from 'react'
import { memoizeApiCall } from './db'
import { getContext, updateUser } from '../apis/user'
import { PRACTICE_LIST_QUERY } from '../gql/practice'
import { GET_CONSOLIDATED_PRACTICES_SLIM_QUERY } from '../gql/consolidatedPractice'
import { ConsolidatedPractice } from '../gql/generated-types/custom-graphql'
import { RequestLevel } from '../constants'
import { gqlClientOptions } from '../gql'
import { isAdminUser, isScratchAdminUser } from './user'
import { normalizeConsolidatedPractice } from './consolidated-practice-normalizer'
import { normalizeContextPractice } from './context-practice-normalizer'
import store from '../store'
import { setContext } from '../slices/auth'

/**
 * Reads the SSOT flag from a context object (non-hook version).
 * Same precedence as useConsolidatedPractice hook.
 */
// TODO [DASH-8441]: Remove this flag check once consolidated practice is GA.
function isConsolidatedPracticeSource(context: Context): boolean {
	return (
		context?.practice?.settings?.useConsolidatedPractice ??
		context?.consolidatedPractice?.settings?.useConsolidatedPractice ??
		context?.consolidator?.featureFlags?.useConsolidatedPractice ??
		false
	)
}

/**
 * Whether or not the current application should have Unified Login enabled, based on the hostname.
 */
export function isUnifiedDomain(): boolean {
	// unified dashboard can be dashboard.scratchpay.com or dashboard.staging.scratchpay.com or 127.0.0.1
	return (
		window.location.hostname.search(/dashboard\.(staging\.)?scratchpay\.com/i) > -1 ||
		window.location.hostname.search('127.0.0.1') > -1 ||
		window.location.hostname.search('localhost') > -1 ||
		window.location.hostname.search(/scratchpaycash-staging.*\.web\.app/i) > -1 || // testing links
		window.location.hostname.search('pre-production-dashboard-getscratch.web.app') > -1 // pre prod
	)
}

// TODO: remove this and use stripeAccountId correctly
export function convertGqlToIPractice(
	prac,
	consolidatedPractice?: IConsolidatedPractice
): IPractice | null {
	if (!prac) {
		return null
	}
	return {
		...prac,
		checkOnly: prac.settings?.checkOnly,
		isCheckEnabled: prac.settings?.isCheckEnabled,
		flags: {
			paymentRequestReminders: prac.settings?.paymentRequestReminders
		},
		settings: {
			...prac.settings,
			...prac.commsSettings
		},
		officeHours: prac.commsSettings?.officeHours,
		serverDrivenTerminal: prac.settings?.serverDrivenTerminal,
		additionalStripeRequirements: prac.settings?.additionalStripeRequirements,
		additionalStripeRequiredInfo: prac.settings?.additionalStripeRequiredInfo,
		additionalStripeFutureRequirements: prac?.stripeFutureRequirements,
		customPayoutTimings: prac.settings?.customPayoutTimings,
		clinicName: prac.name,
		clinicAddress: prac.address,
		clinicState: prac.state,
		clinicCity: prac.city,
		clinicZip: prac.zip,
		clinicCountry: prac.country,
		clinicTimezone: prac.timezone,
		clinicIndustry: prac.industry,
		isChatEnabled: prac.isChatEnabled || prac?.settings?.isChatEnabled,
		dbid: prac.id,
		cuid: prac.stripeAccountId ?? prac.id,
		id: prac.stripeAccountId ?? prac.id, // TODO remove this as we move away from acct_...
		stripeAccountStatus: {
			chargesEnabled: prac.products?.payments?.chargesEnabled,
			payoutsEnabled: prac.products?.payments?.payoutsEnabled
		},
		clinicPhone: prac?.phone ?? consolidatedPractice?.phone,
		consolidatedPractice,
		clinicWebsite: prac?.website ?? prac?.socialUrl?.website,
		clinicGeneralEmail: prac.email,
		clinicHospitalManagerEmail: prac.hospitalManagerEmail,
		clinicPIMsLocationID:
			prac.pimsLocationId ??
			prac.settings?.pimsLocationId ??
			consolidatedPractice?.pimsMetadata?.pimsLocationId,
		currentPims: prac.currentPims ?? consolidatedPractice?.pimsMetadata?.currentPims,
		clinicPIMs: prac.currentPims ?? consolidatedPractice?.pimsMetadata?.currentPims,
		consolidatorReferenceCodeforClinic: prac.consolidatorReferenceCode,
		consolidatorReferenceCodeforClinic2: prac.consolidatorReferenceCode2,
		consolidatorReferenceCodeforClinic3: prac.consolidatorReferenceCode3,
		internalReferenceCode: prac.consolidatorReferenceCode,
		internalReferenceCode2: prac.consolidatorReferenceCode2,
		internalReferenceCode3: prac.consolidatorReferenceCode3,
		regionalManagerId: prac.regionalManagerId,
		regionalMgr: prac.regionalManagerId
	} as IPractice
}

/**
 * Patches the context user with a derived `primaryClinic` field.
 *
 * The context API only returns `primaryClinicId` (prac_ prefix). The frontend
 * (App.tsx `shouldContactSupport`) relies on `user.primaryClinic` (acct_ prefix)
 * to decide whether to render the dashboard or the ContactSupport screen.
 * This helper derives `primaryClinic` from `primaryClinicId` (with a fallback to
 * the first practice) so callers don't have to duplicate the logic.
 */
function patchContextUserPrimaryClinic(user: any): any {
	if (!user) return user
	if (user.primaryClinic) return user

	const firstClinic = user.practices?.[0]?.practice
	const primaryClinicId = user.primaryClinicId ?? firstClinic?.id
	return {
		primaryClinic: primaryClinicId?.replace('prac_', 'acct_'),
		...user
	}
}

/**
 * Validate that the user is correct against the DB and allow them to login
 * @param u the firebase user to validate
 * @returns a Context that includes the User, Practice, and Consolidator retrieved from the DB
 */
export async function validateUser(u: FirebaseUser): Promise<Context | null> {
	try {
		const context = await getContext()
		if (context) {
			if (!context.success) {
				return null
			}

			// Select first practice from user.practices when user.primaryClinicId is empty
			const firstClinic = context.data.user?.practices?.[0]?.practice
			const primaryClinicId = context.data.user?.primaryClinicId ?? firstClinic?.id

			// TODO SLT Reenable for lending-only users
			if (context.data?.user?.uid) {
				const lastLoginDate = new Date()
				const updateUserPayload: any = { lastLoginDate }
				if (!context.data.user?.primaryClinicId && firstClinic?.stripeAccountId) {
					updateUserPayload.primaryClinic = firstClinic.stripeAccountId
				}
				await updateUser(u.uid, updateUserPayload).catch((err) => console.error(err))
			}

			// Hack some context to keep things kosher.
			context.data.user = {
				uid: u.uid,
				email: u.email!,
				emailVerified: u.emailVerified,
				displayName: u.displayName ?? undefined,
				// TODO SLT: These parameters should go away and be replaced.
				primaryClinic: primaryClinicId?.replace('prac_', 'acct_'),
				...context.data.user
			}

			return context.data
		}
	} catch (err) {
		console.error(err)
	}
	return null
}

export function useClinicSelect(context: Context) {
	const { user } = context
	const useConsolidated = isConsolidatedPracticeSource(context)

	return memoizeApiCall(async () => {
		if (useConsolidated) {
			if ((user.isAdmin || user.isScratchAdmin) && user.consolidatorId) {
				const graphQLClient = createClient(
					gqlClientOptions({
						requestLevel: RequestLevel.ADMIN,
						consolidatorId: user.consolidatorId
					})
				)
				const result = await graphQLClient
					.query<{ consolidatedPractices: { items: ConsolidatedPractice[] } }>(
						GET_CONSOLIDATED_PRACTICES_SLIM_QUERY,
						{
							take: null,
							orderBy: [{ name: 'asc' }]
						}
					)
					.toPromise()
				return (result.data?.consolidatedPractices?.items ?? []).map(normalizeConsolidatedPractice)
			}
			// Non-admin users: consolidated practices from context
			return (context.user.consolidatedPractices ?? []).map((prac) =>
				convertGqlToIPractice(prac.practice)
			)
		}

		if (user.isAdmin && !user.isScratchAdmin) {
			// Use practice level because RequestLevel for Practice works for all
			// admin types.
			const graphQLClientOptions = {
				requestLevel: RequestLevel.PRACTICE
			}
			const graphQLClient = createClient(gqlClientOptions(graphQLClientOptions))
			const practiceQuery = graphQLClient
				.query(PRACTICE_LIST_QUERY, {
					take: null,
					orderBy: {
						name: 'asc'
					}
				})
				.toPromise()
			return practiceQuery.then((result) =>
				result.data?.practices.map((prac) => convertGqlToIPractice(prac))
			)
		}
		if (user.isScratchAdmin && user.consolidatorId) {
			const graphQLClient = createClient(
				gqlClientOptions({
					requestLevel: RequestLevel.ADMIN,
					consolidatorId: user.consolidatorId
				})
			)
			return graphQLClient
				.query(PRACTICE_LIST_QUERY, {
					take: null,
					orderBy: {
						name: 'asc'
					}
				})
				.toPromise()
				.then((result) => result.data?.practices.map((prac) => convertGqlToIPractice(prac)))
		}
		return Promise.resolve(
			context.user.practices.map((prac) => convertGqlToIPractice(prac.practice))
		)
	})
}

/**
 * Query the practices in the DB for a scratch admin to avoid querying 20K practices
 * @param inputField the filter criteria
 * @param setOptions the options to set once the query retursn
 * @returns
 */
export function queryPractices(
	inputField: string | undefined,
	options: IPractice[],
	setOptions: Dispatch<SetStateAction<IPractice[]>>,
	additionalParams: any = [],
	setIsLoading?: Dispatch<SetStateAction<boolean>> | undefined,
	// TODO [DASH-8441]: After GA, make the consolidated path the only path and drop this param.
	useConsolidated = false
) {
	// Don't bother
	if (inputField === '' || inputField === undefined) {
		return undefined
	}

	const fetch = async () => {
		if (setIsLoading) {
			setIsLoading(true)
		}

		if (useConsolidated) {
			return queryConsolidatedPractices(inputField, additionalParams, setOptions)
		}

		const graphQLClient = createClient(
			gqlClientOptions({ requestLevel: RequestLevel.SCRATCHADMIN })
		)

		// Check if inputField is a valid integer
		const isVetClinicId = /^\d+$/.test(inputField)
		const orConditions: any[] = []
		if (isVetClinicId) {
			orConditions.push({
				vetClinicId: {
					equals: parseInt(inputField, 10)
				}
			})
		} else {
			orConditions.push(
				{
					name: {
						contains: inputField,
						mode: 'insensitive'
					}
				},
				{
					stripeAccountId: {
						contains: inputField
					}
				},
				{
					id: {
						contains: inputField
					}
				}
			)
		}
		return graphQLClient
			.query(PRACTICE_LIST_QUERY, {
				take: null,
				orderBy: {
					name: 'asc'
				},
				where: {
					AND: [
						{
							OR: orConditions
						},
						...additionalParams
					]
				}
			})
			.then((prac) => {
				if (prac.error) {
					console.error(`Problem querying practices: ${prac.error}`)
					return
				}
				setOptions(
					prac.data?.practices?.map((p) => convertGqlToIPractice(p)).filter(Boolean) as IPractice[]
				)
			})
	}
	return fetch()
		.catch(console.error)
		.finally(() => {
			if (setIsLoading) {
				setIsLoading(false)
			}
		})
}

function queryConsolidatedPractices(
	inputField: string,
	additionalParams: any[],
	setOptions: Dispatch<SetStateAction<IPractice[]>>
) {
	const graphQLClient = createClient(gqlClientOptions({ requestLevel: RequestLevel.SCRATCHADMIN }))

	const isLendingId = /^\d+$/.test(inputField)
	const orConditions: any[] = []
	if (isLendingId) {
		orConditions.push({ lendingId: { equals: parseInt(inputField, 10) } })
	} else {
		orConditions.push(
			{ name: { contains: inputField, mode: 'insensitive' } },
			{ paymentId: { contains: inputField } },
			{ id: { contains: inputField } }
		)
	}

	return graphQLClient
		.query<{ consolidatedPractices: { items: ConsolidatedPractice[] } }>(
			GET_CONSOLIDATED_PRACTICES_SLIM_QUERY,
			{
				take: null,
				orderBy: [{ name: 'asc' }],
				where: {
					AND: [{ OR: orConditions }, ...additionalParams]
				}
			}
		)
		.toPromise()
		.then((result) => {
			if (result.error) {
				console.error(`Problem querying consolidated practices: ${result.error}`)
				return
			}
			setOptions(
				(result.data?.consolidatedPractices?.items ?? [])
					.map(normalizeConsolidatedPractice)
					.filter(Boolean) as IPractice[]
			)
		})
}

export function usePractices(context: Context, cuid: string | undefined) {
	return memoizeApiCall(() => {
		if (cuid === null || cuid === undefined) {
			return Promise.resolve(null)
		}
		if (
			context.practice &&
			(context.practice.id === cuid ||
				context.practice.stripeAccountId === cuid ||
				(context.practice as ConsolidatedPractice)?.paymentId === cuid)
		) {
			const normalized = normalizeContextPractice(context.practice as Record<string, any>)
			return Promise.resolve(convertGqlToIPractice(normalized, context?.consolidatedPractice))
		}
		return getContext(cuid).then((c: any) => {
			if (c?.data) {
				c.data.user = patchContextUserPrimaryClinic(c.data.user)
				store.dispatch(setContext(c.data))
			}
			const normalized = normalizeContextPractice(c.data?.practice)
			return convertGqlToIPractice(normalized, c.data?.consolidatedPractice)
		})
	}, [cuid])
}

// TODO: remove this function, as we should only get the consolidator from the context directly
export function getConsolidator(context: Context, consolidatorId: string | undefined) {
	if (!consolidatorId) {
		return {
			exists: () => false
		}
	}
	if (context?.consolidator?.id === consolidatorId) {
		return {
			exists: () => true,
			data: () => context.consolidator
		}
	}
	if (context?.practice?.consolidator?.id === consolidatorId) {
		return {
			exists: () => true,
			data: () => context.practice.consolidator
		}
	}
	return {
		exists: () => false
	}
}

/**
 * @param context the context for the call, non-optional
 * @param practice the optional practice to use if you have currentPracticeObject
 * @returns whether the user has a defined role for the practice
 */
export const shouldShowSelectRolePage = (
	context: Context,
	practice?: IPractice,
	showPaymentsEnabledForUser = true
): boolean => {
	const user = context?.user

	if (!user || isAdminUser(user) || isScratchAdminUser(user) || !showPaymentsEnabledForUser) {
		return false
	}

	const practiceToCheck = (practice?.id ?? practice?.stripeAccountId) ? practice : context.practice
	const selectedPracticeId =
		practiceToCheck?.dbid ?? practiceToCheck?.id ?? practiceToCheck?.stripeAccountId

	const userPractice = user.practices.find((p) => p?.practice?.id === selectedPracticeId)

	// If it's not a payment practice, don't show the select role banner
	if (!userPractice || !userPractice?.practice?.stripeAccountId) {
		return false
	}

	// Only show the page if user has all roles explicity set as false
	return (
		userPractice?.isRegionalManager === false &&
		userPractice?.isHospitalManager === false &&
		userPractice?.isStaff === false
	)
}

/**
 * @param context the context for the call, non-optional
 * @param practice the optional practice to use if you have currentPracticeObject
 * @returns whether the user is a hospital manager, regional manager, or admin for the context (or override) practice
 */
export function isManagerOrAdmin(context: Context, practice?: IPractice): boolean {
	if (!context.user) {
		return false
	}

	if (
		isScratchAdminUser(context.user) ||
		isAdminUser(context.user) ||
		context.user.isRegionalManager
	) {
		return true
	}

	// Check firestore style
	const practiceToCheck = practice ?? context.practice
	if (practiceToCheck) {
		// Check firestore hmUsers/regionalManagerId by hand
		try {
			if (
				practiceToCheck?.hmUsers?.includes(context.user.uid) ||
				practiceToCheck?.regionalManagerId?.includes(context.user.uid)
			) {
				return true
			}
		} catch (err: any) {
			console.error(err)
		}
	}

	// Check DB style
	if (context.user?.practices?.length > 0) {
		const selectedPracticeId =
			practiceToCheck?.dbid ?? practiceToCheck?.id ?? practiceToCheck?.stripeAccountId
		const poc = context.user.practices.find((p) => p?.practice?.id === selectedPracticeId)
		if (poc && poc.isHospitalManager) {
			return true
		}
	}

	return false
}

/**
 * @param context the context for the call, non-optional
 * @param practice the optional practice to use if you have currentPracticeObject
 * @returns whether the user is a hospital manager for the context (or override) practice
 */
export function isHospitalManager(context: Context, practice?: IPractice): boolean {
	if (!context.user) {
		return false
	}

	// Check firestore style
	const practiceToCheck = practice ?? context.practice
	if (practiceToCheck) {
		// Check firestore hmUsers by hand
		try {
			if (practiceToCheck?.hmUsers?.includes(context.user.uid)) {
				return true
			}
		} catch (err: any) {
			console.error(err)
		}
	}

	// Check postgres style (unified_enabled = true)
	if (context.user?.practices?.length > 0) {
		const selectedPracticeId =
			practiceToCheck?.dbid ?? practiceToCheck?.id ?? practiceToCheck?.stripeAccountId
		const poc = context.user.practices.find((p) => p?.practice?.id === selectedPracticeId)
		return poc && !!poc.isHospitalManager
	}

	return false
}

export function getTransactionFees(practice: IPractice): IPracticeTransactionFees | undefined {
	if (practice?.transactionFeeAmt) {
		return {
			amount: practice.transactionFeeAmt,
			percentage: practice.transactionFeePct,
			areNet: !!(practice?.transactionFeesAreNet || 0),
			feeTier: practice?.transactionFeeTier,
			storedCardPct: practice?.transactionFees?.storedCardPct,
			networkTokenFee: practice?.transactionFees?.networkTokenFee
		}
	}
	return practice?.transactionFees
}
